<template>
  <section class="account-cellar no-border-bottom">
    <CellarHeader active-link="wines" />

    <div class="site-width site-width-full">
      <div class="account-cellar--content">
        <div class="account-cellar--my-wines active">
          <div class="account-cellar--utilities row">
            <div>
              <div class="search columns columns small-12 large-4">
                <label for="cellar-search" class="sr-only">
                  Search Wine Access
                </label>
                <input
                  id="cellar-search"
                  v-model="searchText"
                  type="search"
                  placeholder="Search (name, varietal, vintage, etc.)"
                  @keypress="handleEnter"
                />
                <button v-if="searchText" class="clear" @click="clearSearch">
                  <span class="sr-only">Clear Search</span>
                </button>
              </div>
              <div
                class="filters applied-sort-container columns small-12 large-8"
              >
                <div class="filter-row">
                  <div class="plp-filter">
                    <div
                      v-click-outside="clickOutsideFilter"
                      role="region"
                      class="filter-option"
                    >
                      <button
                        class="filter-trigger"
                        :class="{ active: filterMenuOpen }"
                        aria-haspopup="true"
                        :aria-expanded="filterMenuOpen"
                        aria-controls="filter_menu"
                        @click="filterMenuOpen = !filterMenuOpen"
                      >
                        <span id="filter_by"> Filter by: </span>
                        <span class="sr-only"> Currently filtering by </span>
                        <strong>
                          {{ filterOptions[currentFilter] }}
                        </strong>
                        <span class="arrow"></span>
                      </button>
                      <div
                        id="filter_menu"
                        aria-labelledby="filter_by"
                        class="submenu"
                        :class="{ active: filterMenuOpen }"
                      >
                        <div class="submenu-content">
                          <ul role="menu">
                            <li
                              v-for="[
                                filterValue,
                                filterDisplay,
                              ] in Object.entries(filterOptions)"
                              :id="`filter_by_${filterValue}`"
                              :key="`filter_by_${filterValue}`"
                            >
                              <nuxt-link
                                :class="
                                  currentFilter === filterValue ? 'active' : ''
                                "
                                :to="filteringUrl(filterValue)"
                                role="menuitem"
                                @click="filterMenuOpen = false"
                              >
                                {{ filterDisplay }}
                              </nuxt-link>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="plp-sort">
                    <div
                      v-click-outside="clickOutsideSort"
                      role="region"
                      class="sort-option"
                    >
                      <button
                        class="sort-trigger"
                        :class="{ active: sortMenuOpen }"
                        aria-haspopup="true"
                        :aria-expanded="sortMenuOpen"
                        aria-controls="sort_menu"
                        @click="sortMenuOpen = !sortMenuOpen"
                      >
                        <span id="sort_by">Sort by: </span>
                        <span class="sr-only"> Currently sorting by </span>
                        <strong>
                          {{ sortOptions[currentSort] }}
                        </strong>
                        <span class="arrow"></span>
                      </button>
                      <div
                        id="sort_menu"
                        aria-labelledby="sort_by"
                        class="submenu"
                        :class="{ active: sortMenuOpen }"
                      >
                        <div class="submenu-content">
                          <ul role="menu">
                            <li
                              v-for="[sortValue, sortDisplay] in Object.entries(
                                sortOptions,
                              )"
                              :id="`sort_by_${sortValue}`"
                              :key="`sort_by_${sortValue}`"
                            >
                              <nuxt-link
                                :class="
                                  currentSort === sortValue ? 'active' : ''
                                "
                                :to="orderingUrl(sortValue)"
                                role="menuitem"
                                @click="sortMenuOpen = false"
                              >
                                {{ sortDisplay }}
                              </nuxt-link>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="account-cellar--list">
            <div v-if="showEmpty && currentKeyword" class="empty">
              <h6>
                <b>0</b> matches for "{{ currentKeyword }}".
                <a href="#" @click.prevent="clearSearch">Clear Search</a>
              </h6>
            </div>
            <div
              v-else-if="showEmpty && currentFilter === 'all'"
              class="empty center"
            >
              <h5>
                There are no wines that have been shipped to you yet.
                <br />
                <br />
                You could change that by checking out our
                <nuxt-link
                  to="/store/occasion/wine-team-favorites/"
                  class="empty-link"
                >
                  wine team's favorite wines
                </nuxt-link>
                and purchasing yourself a case.
              </h5>
            </div>
            <div
              v-else-if="showEmpty && currentFilter === 'wineclub'"
              class="empty center"
            >
              <h6>
                Our Wine Club is rated by the The New York Times Wirecutter's as
                the #1 Wine Club.
                <nuxt-link to="/club/" class="empty-link"
                  >Check it out</nuxt-link
                >
              </h6>
            </div>
            <div v-else-if="showEmpty" class="empty center">
              <h6>
                There are no {{ filterOptions[currentFilter]
                }}{{ currentFilter === 'available' ? ' Wines ' : ' ' }}that have
                been shipped to you yet.
                <br />
                <br />
                <nuxt-link :to="filteringUrl('all')" class="empty-link">
                  Clear Filter
                </nuxt-link>
              </h6>
            </div>
            <Accordion v-slot="scope">
              <ProductReviewAccordionItem
                v-for="review in productReviews"
                :key="review.product_id"
                :review="review"
                :expanded-by-default="
                  myCellarPageFirstLoad && productReviews.indexOf(review) === 0
                "
                :error="errors[review.product_id]"
                @expanded="scope.expanded"
                @set-notifications="setNotifications(review, $event)"
                @set-review="setReview(review, $event)"
                @add-to-cart="addToCart(review)"
                @add-clicked="startSpinner"
                @suggestions-add-to-cart="stopSpinner"
              />
            </Accordion>
          </div>
        </div>
      </div>
    </div>
    <NotifyMeModal
      :visible="notifyMeModalOpen"
      :product-id="notifyProductId"
      @closed="notifyMeModalOpen = false"
    />
    <PaginationControls
      :url-for-page-func="(page) => paramChangeUrl({ page })"
      :current-page-number="currentPage"
      :total-pages="pageCount"
    />
  </section>
</template>

<script setup>
import { disableNotificationService } from '#common/utils/catalog'
import { baseURL } from '#common/utils/url'
import { markAllProductsViewed } from '~/services/notificationServices'

const $route = useRoute()
const $router = useRouter()
const { $auth, $axios, $store } = useNuxtApp()
const { isLoading } = storeToRefs(useLayout())

$auth.requireLogin()

const decodeSearchQuery = (query) => {
  if (!query) return ''
  // First, decode standard URI components (%2B -> +, %20 -> space, etc.)
  const decoded = decodeURIComponent(query)
  // Then, specifically replace '+' characters with spaces.
  return decoded.replace(/\+/g, ' ')
}

// Data properties
const count = ref(0)
const searchText = ref(decodeSearchQuery($route.query.search))
const sortMenuOpen = ref(false)
const filterMenuOpen = ref(false)
const currentFilter = ref($route.query.filter || 'all')
const currentSort = ref($route.query.sort || '-purchased')
const productReviews = ref([])
const notifyMeModalOpen = ref(false)
const notifyProductId = ref(null)
const errors = ref({})
const timesFetched = ref(0)

const filterOptions = {
  all: 'All Wines',
  rated: 'Rated Wines',
  unrated: 'Unrated Wines',
  available: 'Available',
  wineclub: 'Wine Club wines',
}

const sortOptions = {
  '-purchased': 'Purchase Date',
  title: 'Wine Name',
  drinking_window: 'Drinking Window',
  '-rating': 'Average Rating',
  '-my_rating': 'My Rating',
}

// Helper functions
const paramsFromRoute = (route) => {
  const params = {
    filter_by: route.query.filter || 'all',
    page: parseInt(route.query.page || '1'),
    keyword: decodeSearchQuery(route.query.search),
    order_by: route.query.sort || '-purchased',
  }
  if (route.params.slug) {
    params.product = route.params.slug.split('_')[1]
  }
  return params
}

const urlFromParams = (params) => {
  const queryParams = new URLSearchParams()

  if (params.filter_by) {
    queryParams.set('filter', params.filter_by)
  }
  if (params.keyword) {
    queryParams.set('search', params.keyword)
  }
  if (params.page) {
    queryParams.set('page', params.page)
  }
  if (params.order_by) {
    queryParams.set('sort', params.order_by)
  }

  const queryString = queryParams.toString()
  return queryString ? `/cellar/wines/?${queryString}` : '/cellar/wines/'
}

// Computed properties
const currentPage = computed(() => parseInt($route.query.page || '1'))
const pageCount = computed(() => Math.ceil(count.value / 20))
const currentKeyword = computed(() => decodeSearchQuery($route.query.search))
const showEmpty = computed(
  () => productReviews.value.length === 0 && !isLoading.value,
)
const allProductNotificationsViewed = computed(
  () => $store.getters.allProductNotificationsViewed,
)
const myCellarPageFirstLoad = computed(
  () => $store.getters.myCellarPageFirstLoad,
)

// Methods
const startSpinner = () => {
  isLoading.value = true
}

const stopSpinner = () => {
  isLoading.value = false
}

const clickOutsideSort = () => {
  sortMenuOpen.value = false
}

const clickOutsideFilter = () => {
  filterMenuOpen.value = false
}

const paramChangeUrl = (overwrites) => {
  const baseParams = {
    filter_by: currentFilter.value,
    keyword: searchText.value,
    order_by: currentSort.value,
    page: currentPage.value,
  }
  return urlFromParams(Object.assign(baseParams, overwrites))
}

const orderingUrl = (value) => {
  return paramChangeUrl({ order_by: value, page: 1 })
}

const filteringUrl = (value) => {
  return paramChangeUrl({ filter_by: value, page: 1 })
}

const search = async () => {
  const decodedUrlSearch = decodeSearchQuery($route.query.search)
  if (searchText.value !== decodedUrlSearch) {
    await $router.push(paramChangeUrl({ keyword: searchText.value, page: 1 }))
  }
}

const handleEnter = (event) => {
  if (event.keyCode === 13) {
    search()
  }
}

const clearSearch = async () => {
  searchText.value = ''
  await search()
}

const setNotifications = (review, notifyMe) => {
  if (!notifyMe) {
    disableNotification(review)
  } else {
    setNotifyMeModalOpen(review)
  }
}

const setNotifyMeModalOpen = (review) => {
  notifyProductId.value = review.product_id
  notifyMeModalOpen.value = true
}

const disableNotification = async (review) => {
  errors.value[review.product_id] = null
  try {
    await disableNotificationService($axios, review.product_id, $auth.user)
  } catch (error) {
    console.error(error)
    errors.value[review.product_id] = 'Error disabling notifications'
  }
}

const setReview = async (review, { user_rating, user_review }) => {
  startSpinner()
  errors.value[review.product_id] = null

  try {
    const result = await $axios.post('/api/customer/portfolio/', {
      product_id: review.product_id,
      user_rating,
      user_review,
    })

    const reviewIndex = productReviews.value.findIndex(
      (r) => r.product_id === review.product_id,
    )

    productReviews.value[reviewIndex] = {
      ...productReviews.value[reviewIndex],
      user_rating: result.data.user_rating,
      user_review: result.data.user_review,
      review_credited: result.data.review_credited,
      average_rating: result.data.average_rating,
    }
  } catch (error) {
    console.error(error)
    errors.value[review.product_id] = 'Error saving the review and rating'
  } finally {
    stopSpinner()
  }
}

const addToCart = async (review) => {
  startSpinner()
  errors.value[review.product_id] = null

  try {
    await $store.dispatch('addToCart', {
      quantity: 1,
      product_id: review.product_id,
      source_url: baseURL() + $route.fullPath,
    })
  } catch (error) {
    console.error(error)
    let theError = error.reason
    if (error.reason.non_field_errors) {
      theError = error.reason.non_field_errors[0]
    }
    errors.value[review.product_id] = theError
  } finally {
    stopSpinner()
  }
}

// Fetch data
const fetchData = async () => {
  if (!$auth.loggedIn) {
    productReviews.value = []
    count.value = 0
    stopSpinner()
    return
  }

  currentFilter.value = $route.query.filter || 'all'
  currentSort.value = $route.query.sort || '-purchased'
  startSpinner()

  try {
    const params = paramsFromRoute($route)
    const result = await $axios.get('/api/customer/portfolio/', { params })
    productReviews.value = result.data.products
    timesFetched.value += 1
    count.value = result.data.count
  } catch (e) {
    console.error(e)
  } finally {
    stopSpinner()
  }
}

// Watchers
watch(() => $route.fullPath, fetchData)
watch(() => $auth.loggedIn, fetchData)
watch(productReviews, () => {
  if (timesFetched.value > 1) {
    $store.dispatch('setMyCellarPageFirstLoad', false)
  }
})

watch(
  () => $route.query.search,
  (newSearchQuery) => {
    const correctlyDecoded = decodeSearchQuery(newSearchQuery)
    if (searchText.value !== correctlyDecoded) {
      searchText.value = correctlyDecoded
    }
  },
)

// Lifecycle hooks
onActivated(async () => {
  fetchData()
  await nextTick(() => {
    if (!allProductNotificationsViewed.value) {
      $store.dispatch('setAllProductNotificationsViewed', true)
      markAllProductsViewed($axios)
    }
  })
})

onUnmounted(() => {
  $store.dispatch('setMyCellarPageFirstLoad', true)
})
</script>
